import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as forecastApi from '../api';
import type { ForecastTriggerRequest } from '../types';

export const forecastKeys = {
  all: ['forecasts'] as const,
  latest: (sensorId: string, organisationId?: string) =>
    [...forecastKeys.all, 'latest', sensorId, organisationId] as const,
  history: (sensorId: string, params?: Record<string, unknown>) =>
    [...forecastKeys.all, 'history', sensorId, params] as const,
  sensors: (organisationId: string, search: string) =>
    [...forecastKeys.all, 'sensors', organisationId, search] as const,
  anomalies: (params?: Record<string, unknown>) =>
    [...forecastKeys.all, 'anomalies', params] as const,
  anomalySummary: (params?: Record<string, unknown>) =>
    [...forecastKeys.all, 'anomaly-summary', params] as const,
};

/**
 * Get latest forecast for a sensor
 */
export function useLatestForecast(sensorId: string, organisationId?: string) {
  return useQuery({
    queryKey: forecastKeys.latest(sensorId, organisationId),
    queryFn: () => forecastApi.getLatestForecast(sensorId, organisationId),
    enabled: !!sensorId && !!organisationId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get forecast history for a sensor
 */
export function useForecastHistory(
  sensorId: string,
  params?: { organisationId?: string; from?: string; to?: string; forecastType?: string },
) {
  return useQuery({
    queryKey: forecastKeys.history(sensorId, params as Record<string, unknown>),
    queryFn: () => forecastApi.getForecastHistory(sensorId, params),
    enabled: !!sensorId && !!params?.organisationId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Trigger an on-demand forecast
 */
export function useTriggerForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sensorId, organisationId, params }: { sensorId: string; organisationId: string; params?: ForecastTriggerRequest }) =>
      forecastApi.triggerForecast(sensorId, params, organisationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: forecastKeys.latest(variables.sensorId, variables.organisationId) });
      toast.success('Forecast generated');
    },
    onError: () => {
      toast.error('Failed to generate forecast');
    },
  });
}

/**
 * Get anomaly events
 */
export function useAnomalies(params?: {
  organisationId?: string;
  sensorId?: string;
  from?: string;
  to?: string;
  anomalyType?: string;
  severity?: string;
  pageNumber?: number;
  pageLength?: number;
}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: forecastKeys.anomalies(params as Record<string, unknown>),
    queryFn: () => forecastApi.getAnomalies(params),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * List forecastable sensors in an organisation.
 */
export function useForecastableSensors(organisationId: string | undefined, search: string) {
  return useQuery({
    queryKey: forecastKeys.sensors(organisationId ?? '', search),
    queryFn: () => forecastApi.getForecastableSensors(organisationId!, search),
    enabled: !!organisationId,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}

/**
 * Get anomaly summary
 */
export function useAnomalySummary(
  params?: { organisationId?: string; from?: string; to?: string },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: forecastKeys.anomalySummary(params as Record<string, unknown>),
    queryFn: () => forecastApi.getAnomalySummary(params),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}
